<template>
  <div>
    <section
      v-if="property"
      class="n-section-primary !tw-pb-0 !tw-pt-36"
      :style="{
        backgroundColor: vars.backgroundColor ? `var(--c-${vars.backgroundColor})` : 'var(--c-section_secondary)',
      }"
    >
      <!-- GALLERY: START -->
      <template v-if="vars.galleryVersionText === 'v2'">
        <div
          v-if="property.is_rent"
          class="tw-mt-16 tw-w-full"
          :class="!isMobileDevice ? 'tw-grid tw-grid-cols-12 tw-gap-x-3' : ''"
        >
          <div
            v-if="!isMobileDevice"
            class="tw-col-span-12 tw-h-full tw-w-full lg:tw-col-span-6"
            @click="onClickedImage(null)"
          >
            <library-image
              v-if="primaryFivePhotos[0].background"
              :src="primaryFivePhotos[0].background"
              class="tw-h-full tw-cursor-pointer"
              alt="property first image"
              :title="property.advert_heading"
              @click="onClickedImage(primaryFivePhotos[0])"
              :design-width="964"
              :design-height="643"
              :style="`border-radius: var(--rounded)`"
            >
              <template #overlay-item>
                <div class="nc-first-image-overlay-item">
                  <div v-if="property && property.medias" style="" class="tw-text-md tw-px-2 tw-py-1">
                    1/{{ property.medias.length }}
                  </div>
                </div>
              </template>
            </library-image>
          </div>
          <div
            v-if="!isMobileDevice"
            class="tw-col-span-12 tw-grid tw-h-full tw-grid-cols-12 tw-gap-x-3 md:tw-col-span-6"
          >
            <div class="tw-col-span-6 tw-flex tw-flex-row tw-gap-y-3 md:tw-flex-col">
              <library-image
                v-if="primaryFivePhotos[1].background"
                :src="primaryFivePhotos[1].background"
                alt="property first image"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[1])"
                :title="property.advert_heading"
                :design-width="364"
                :design-height="246"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter" 
                :pre-market-blur-text="preMarketBlurText"
              />

              <library-image
                v-if="primaryFivePhotos[2].background"
                :src="primaryFivePhotos[2].background"
                alt="property second image"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[2])"
                :title="property.advert_heading"
                :design-width="364"
                :design-height="246"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter" 
                :pre-market-blur-text="preMarketBlurText"
              />
            </div>
            <div class="tw-col-span-6 tw-flex tw-flex-row tw-gap-y-3 md:tw-flex-col">
              <library-image
                v-if="primaryFivePhotos[3].background"
                :src="primaryFivePhotos[3].background"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[3])"
                :design-width="364"
                :design-height="246"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter" 
                :pre-market-blur-text="preMarketBlurText"
              />
              <library-image
                v-if="primaryFivePhotos[4].background"
                :src="primaryFivePhotos[4].background"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[4])"
                :design-width="364"
                :design-height="246"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter" 
                :pre-market-blur-text="preMarketBlurText"
              />
            </div>
          </div>
          <div v-else-if="isMobileDevice" class="n-main-container tw-w-screen">
            <Swiper
              :modules="[SwiperNavigation]"
              :pagination="true"
              :slides-per-view="1"
              :space-between="1"
              effect="fade"
              :clickable="true"
              :navigation="{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }"
              :loop="true"
              class="tw-w-full"
              @swiper="onSwiper"
            >
              <SwiperSlide v-for="(item, index) in primaryFivePhotos" :key="index" class="tw-w-full">
                <library-image :src="item.background" @click="onClickedImage(item)" :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText"/>
              </SwiperSlide>
              <div class="tw-flex tw-items-center tw-justify-between">
                <div class="swiper-button-prev tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3">
                  <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18.75 7.5H5.25C4.83 7.5 4.5 7.17 4.5 6.75C4.5 6.33 4.83 6 5.25 6H18.75C19.17 6 19.5 6.33 19.5 6.75C19.5 7.17 19.17 7.5 18.75 7.5Z"
                      fill="black"
                    />
                    <path
                      d="M9 12.75C8.90164 12.7512 8.80411 12.7318 8.71369 12.6931C8.62328 12.6543 8.54197 12.5971 8.475 12.525L3.225 7.275C2.925 6.975 2.925 6.51 3.225 6.21L8.475 0.975C8.775 0.675 9.24 0.675 9.54 0.975C9.84 1.275 9.84 1.74 9.54 2.04L4.815 6.765L9.54 11.49C9.84 11.79 9.84 12.255 9.54 12.555C9.39 12.705 9.195 12.78 9.015 12.78L9 12.75Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div class="swiper-button-next tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3">
                  <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 7.5H18.75C19.17 7.5 19.5 7.17 19.5 6.75C19.5 6.33 19.17 6 18.75 6H5.25C4.83 6 4.5 6.33 4.5 6.75C4.5 7.17 4.83 7.5 5.25 7.5Z"
                      fill="black"
                    />
                    <path
                      d="M15 12.75C15.0984 12.7512 15.1959 12.7318 15.2863 12.6931C15.3767 12.6543 15.458 12.5971 15.525 12.525L20.775 7.275C21.075 6.975 21.075 6.51 20.775 6.21L15.525 0.975C15.225 0.675 14.76 0.675 14.46 0.975C14.16 1.275 14.16 1.74 14.46 2.04L19.185 6.765L14.46 11.49C14.16 11.79 14.16 12.255 14.46 12.555C14.61 12.705 14.805 12.78 14.985 12.78L15 12.75Z"
                      fill="black"
                    />
                  </svg>
                </div>
              </div>
            </Swiper>
          </div>
        </div>
        <!-- Sale -->
        <div v-else class="tw-w-screen tw-gap-x-1 tw-px-4">
          <Swiper
            :modules="[SwiperNavigation]"
            :pagination="true"
            :centered-slides="true"
            :center-insufficient-slides="true"
            :loop="true"
            :initial-slide="0"
            :slides-per-view="2"
            :space-between="16"
            effect="fade"
            :clickable="true"
            :navigation="{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }"
            :breakpoints="{
              320: {
                slidesPerView: 1,
                spaceBetween: 8,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
            }"
            class="tw-w-full lg:tw-w-[100%]"
            @swiper="onSwiper"
          >
            <SwiperSlide
              v-for="(item, index) in property.medias.filter(
                (media) => !media.isVirtualTour && !media.background.includes('ytimg'),
              )"
              :key="index"
            >
              <div v-if="!item.background.includes('ytimg')">
                <library-image
                  v-if="!item.isVirtualTour || (!item.background.includes('ytimg') && item.background)"
                  :src="item.background"
                  @click="onClickedImage(item)"
                  :design-width="964"
                  :design-height="643"
                  :blur="imageBlurFilter" 
                  :pre-market-blur-text="preMarketBlurText"
                >
                  <template #overlay-item>
                    <div v-if="index === 0">
                      <div class="nc-first-image-overlay-item">
                        <div v-if="property && property.medias" style="" class="tw-text-md tw-px-2 tw-py-1">
                          1/{{ property.medias.length }}
                        </div>
                      </div>
                    </div>
                  </template>
                </library-image>
              </div>
            </SwiperSlide>

            <div class="tw-flex tw-items-center tw-justify-between">
              <div
                class="swiper-button-prev tw-cursor-pointer tw-rounded-full tw-bg-white tw-px-2 tw-py-5 lg:!tw-left-16 lg:tw-px-2"
              >
                <svg width="33" height="12" viewBox="0 0 33 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M31.5 5.25H32.25V6.75H31.5V5.25ZM0.96967 6.53033C0.676777 6.23744 0.676777 5.76256 0.96967 5.46967L5.74264 0.696699C6.03553 0.403806 6.51041 0.403806 6.8033 0.696699C7.09619 0.989592 7.09619 1.46447 6.8033 1.75736L2.56066 6L6.8033 10.2426C7.09619 10.5355 7.09619 11.0104 6.8033 11.3033C6.51041 11.5962 6.03553 11.5962 5.74264 11.3033L0.96967 6.53033ZM31.5 6.75H1.5V5.25H31.5V6.75Z"
                    fill="#545454"
                  />
                </svg>
              </div>
              <div
                class="swiper-button-next tw-cursor-pointer tw-rounded-full tw-bg-white tw-px-2 tw-py-5 lg:!tw-right-16 lg:tw-px-2"
              >
                <svg width="33" height="12" viewBox="0 0 33 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.5 5.25H0.75V6.75H1.5V5.25ZM32.0303 6.53033C32.3232 6.23744 32.3232 5.76256 32.0303 5.46967L27.2574 0.696699C26.9645 0.403806 26.4896 0.403806 26.1967 0.696699C25.9038 0.989593 25.9038 1.46447 26.1967 1.75736L30.4393 6L26.1967 10.2426C25.9038 10.5355 25.9038 11.0104 26.1967 11.3033C26.4896 11.5962 26.9645 11.5962 27.2574 11.3033L32.0303 6.53033ZM1.5 6.75H31.5V5.25H1.5V6.75Z"
                    fill="#545454"
                  />
                </svg>
              </div>
            </div>
          </Swiper>
        </div>
      </template>
      <!-- GALLERY: END -->

      <div class="property-detail-info tw-flex tw-w-full tw-flex-col tw-py-12">
        <div class="tw-flex tw-justify-between tw-pb-6">
          <library-properties-back-to-all-properties-navbar :text="vars.backButtonText" color="var(--c-primary)" />
        </div>
        <div class="tw-grid tw-grid-cols-12 tw-items-start tw-justify-between">
          <div class="tw-col-span-12 tw-flex tw-flex-col lg:tw-col-span-7">
            <h2>{{ property.advert_heading }}</h2>
            <div class="address tw-mt-2 tw-flex tw-items-center tw-gap-1">
              <p class="tw-capitalize">{{ property.display_address }}</p>
            </div>
            <div v-if="!property.is_locked" class="tw-mt-3 tw-flex tw-items-center tw-gap-1">
              <div class="rooms tw-flex tw-flex-row tw-items-center tw-gap-4">
                <p>{{ property.bedroom }} BED</p>
                <p>{{ property.bathroom }} BATH</p>
                <p>{{ property.reception }} REC</p>
              </div>
            </div>
            <div class="tw-flex tw-flex-col tw-items-start tw-gap-5 tw-py-5 md:tw-flex-row md:tw-items-center">
              <button v-if="!property.is_locked" class="n-primary" @click="onSendLead()">BOOK A VIEWING</button>
              <!-- <button @click="href('cinematic_videography')" class="n-secondary">CINEMATIC VIDEOGRAPHY</button> -->
              <div
                class="tw-flex tw-h-12 tw-w-12 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full"
                style="background-color: transparent; border: 1px solid var(--c-primary)"
                @click="onShared()"
              >
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M20.5386 3C19.9534 2.9998 19.3753 3.12802 18.845 3.37562C18.3147 3.62321 17.8452 3.98414 17.4697 4.43296C17.0941 4.88177 16.8216 5.40752 16.6713 5.97314C16.5211 6.53875 16.4968 7.13044 16.6002 7.70646L10.7786 11.7828C10.7495 11.8029 10.7216 11.8247 10.6949 11.848C10.1043 11.3863 9.39588 11.0999 8.65036 11.0214C7.90484 10.9429 7.15226 11.0754 6.47844 11.404C5.80462 11.7325 5.23669 12.2438 4.83939 12.8795C4.4421 13.5152 4.23145 14.2497 4.23145 14.9994C4.23145 15.749 4.4421 16.4836 4.83939 17.1193C5.23669 17.755 5.80462 18.2663 6.47844 18.5948C7.15226 18.9233 7.90484 19.0559 8.65036 18.9774C9.39588 18.8989 10.1043 18.6125 10.6949 18.1508C10.7215 18.1745 10.7495 18.1967 10.7786 18.2172L16.6002 22.2935C16.4273 23.2569 16.6137 24.2502 17.1241 25.0854C17.6344 25.9206 18.4333 26.5397 19.3694 26.8255C20.3055 27.1113 21.3139 27.0439 22.2037 26.6362C23.0935 26.2284 23.8029 25.5086 24.1976 24.613C24.5923 23.7173 24.645 22.708 24.3456 21.7762C24.0461 20.8443 23.4155 20.0546 22.5729 19.5565C21.7304 19.0584 20.7344 18.8865 19.7737 19.0734C18.8129 19.2603 17.954 19.7929 17.3595 20.5705L11.8469 16.712C12.0931 16.1926 12.2309 15.6129 12.2309 15C12.2309 14.3871 12.0931 13.8062 11.8469 13.288L17.3608 9.42831C17.7738 9.96895 18.3179 10.3953 18.9416 10.6671C19.5654 10.9388 20.2481 11.047 20.9253 10.9813C21.6024 10.9157 22.2517 10.6784 22.8116 10.2919C23.3715 9.90545 23.8236 9.38253 24.1251 8.77263C24.4266 8.16273 24.5676 7.48601 24.5347 6.80646C24.5017 6.1269 24.296 5.46697 23.937 4.88907C23.5779 4.31117 23.0774 3.8344 22.4828 3.50385C21.8881 3.17329 21.219 2.99988 20.5386 3ZM18.3848 7C18.3848 6.42876 18.6117 5.88093 19.0156 5.477C19.4195 5.07308 19.9674 4.84615 20.5386 4.84615C21.1099 4.84615 21.6577 5.07308 22.0616 5.477C22.4655 5.88093 22.6925 6.42876 22.6925 7C22.6925 7.57124 22.4655 8.11907 22.0616 8.523C21.6577 8.92692 21.1099 9.15385 20.5386 9.15385C19.9674 9.15385 19.4195 8.92692 19.0156 8.523C18.6117 8.11907 18.3848 7.57124 18.3848 7ZM8.23093 12.8462C7.6597 12.8462 7.11186 13.0731 6.70793 13.477C6.30401 13.8809 6.07709 14.4288 6.07709 15C6.07709 15.5712 6.30401 16.1191 6.70793 16.523C7.11186 16.9269 7.6597 17.1538 8.23093 17.1538C8.80217 17.1538 9.35001 16.9269 9.75393 16.523C10.1579 16.1191 10.3848 15.5712 10.3848 15C10.3848 14.4288 10.1579 13.8809 9.75393 13.477C9.35001 13.0731 8.80217 12.8462 8.23093 12.8462ZM20.5386 20.8462C19.9674 20.8462 19.4195 21.0731 19.0156 21.477C18.6117 21.8809 18.3848 22.4288 18.3848 23C18.3848 23.5712 18.6117 24.1191 19.0156 24.523C19.4195 24.9269 19.9674 25.1538 20.5386 25.1538C21.1099 25.1538 21.6577 24.9269 22.0616 24.523C22.4655 24.1191 22.6925 23.5712 22.6925 23C22.6925 22.4288 22.4655 21.8809 22.0616 21.477C21.6577 21.0731 21.1099 20.8462 20.5386 20.8462Z"
                    fill="var(--c-primary)"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            class="tw-col-span-12 tw-flex tw-flex-col tw-justify-between lg:tw-col-span-5 lg:tw-items-end lg:tw-justify-end lg:tw-gap-8"
          >
            <div
              class="tw-flex tw-w-full tw-items-center tw-justify-between max-lg:tw-pt-4 lg:tw-justify-end lg:tw-gap-8"
            >
              <div v-if="property.price">
                <p v-if="property.price.type.name" class="tw-font-bold tw-uppercase">
                  {{ property.price.type.name }}
                </p>
                <h5 class="!tw-text-3xl">{{ property.price.price }}</h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- variant v1 for gallery -->
      <template v-if="vars.galleryVersionText === 'v1'">
        <div v-if="featuredVideo" class="tw-w-screen tw-px-4 tw-py-5">
          <div v-if="!isMobileDevice" class="tw-grid tw-grid-cols-[2.25fr_6.5fr_3.25fr] tw-gap-5">
            <div class="tw-relative">
              <div class="tw-aspect-[9/16] tw-h-full tw-w-full">
                <library-iframe
                  class="tw-absolute tw-inset-0 tw-h-full tw-w-full"
                  :src="featuredVideo"
                  frameborder="0"
                  allowfullscreen
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                ></library-iframe>
              </div>
            </div>

            <div>
              <library-image
                v-if="primaryFivePhotos[0].background"
                :src="primaryFivePhotos[0].background"
                class="tw-h-full tw-cursor-pointer"
                alt="property first image"
                :title="property.advert_heading"
                @click="onClickedImage(primaryFivePhotos[0])"
                :design-width="998"
                :design-height="659"
                :style="`border-radius: var(--rounded);`"
                :blur="imageBlurFilter"
                :pre-market-blur-text="preMarketBlurText"
              >
                <template #overlay-item>
                  <div class="nc-first-image-overlay-item">
                    <div
                      v-if="property && property.medias"
                      style="
                        border-radius: var(--rounded-sm);
                        color: var(--c-text-primary);
                        background-color: var(--c-section_primary);
                      "
                      class="tw-text-md tw-px-2 tw-py-1"
                    >
                      1/{{ property.medias.length }}
                    </div>
                  </div>
                </template>
              </library-image>
            </div>

            <div class="tw-flex tw-flex-col tw-justify-between">
              <library-image
                v-if="primaryFivePhotos[1].background"
                :src="primaryFivePhotos[1].background"
                alt="property first image"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[1])"
                :title="property.advert_heading"
                :design-width="472"
                :design-height="319"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter"
                :pre-market-blur-text="preMarketBlurText"
              />

              <library-image
                v-if="primaryFivePhotos[2].background"
                :src="primaryFivePhotos[2].background"
                alt="property first image"
                class="tw-cursor-pointer"
                @click="onClickedImage(primaryFivePhotos[2])"
                :title="property.advert_heading"
                :design-width="472"
                :design-height="319"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter"
                :pre-market-blur-text="preMarketBlurText"
              >
                <template #overlay-item>
                  <div
                    v-if="property && property.medias"
                    class="tw-absolute tw-inset-0 tw-z-10 tw-h-full tw-w-full tw-bg-black tw-opacity-30"
                    style="border-radius: var(--rounded-sm)"
                  ></div>
                  <button
                    class="tw-z-20 tw-px-4 tw-py-2"
                    style="background-color: var(--c-input); color: var(--c-text-secondary)"
                  >
                    SEE ALL PHOTOS
                  </button>
                </template>
              </library-image>
            </div>
          </div>
          <div v-else-if="isMobileDevice">
            <div class="tw-relative tw-pb-4">
              <div class="tw-aspect-[9/16] tw-h-full tw-w-full">
                <library-iframe
                  class="tw-absolute tw-inset-0 tw-h-full tw-w-full"
                  :src="property.featured_video?.mediaLink"
                  style="border-radius: var(--rounded)"
                  :w-ratio="9"
                  :h-ratio="16"
                  frameborder="0"
                  allowfullscreen
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                ></library-iframe>
              </div>
            </div>
            <Swiper
              :modules="[SwiperNavigation]"
              :pagination="true"
              :slides-per-view="1"
              :space-between="1"
              effect="fade"
              :clickable="true"
              :loop="true"
              :navigation="{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }"
              class="tw-mt-6 tw-w-full"
              @swiper="onSwiper"
            >
              <SwiperSlide v-for="(item, index) in primaryFivePhotos" :key="index" class="tw-w-full">
                <library-image :src="item.background" @click="onClickedImage(item)" :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText"/>
              </SwiperSlide>
              <div class="tw-flex tw-items-center tw-justify-between">
                <div class="swiper-button-prev tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3">
                  <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18.75 7.5H5.25C4.83 7.5 4.5 7.17 4.5 6.75C4.5 6.33 4.83 6 5.25 6H18.75C19.17 6 19.5 6.33 19.5 6.75C19.5 7.17 19.17 7.5 18.75 7.5Z"
                      fill="black"
                    />
                    <path
                      d="M9 12.75C8.90164 12.7512 8.80411 12.7318 8.71369 12.6931C8.62328 12.6543 8.54197 12.5971 8.475 12.525L3.225 7.275C2.925 6.975 2.925 6.51 3.225 6.21L8.475 0.975C8.775 0.675 9.24 0.675 9.54 0.975C9.84 1.275 9.84 1.74 9.54 2.04L4.815 6.765L9.54 11.49C9.84 11.79 9.84 12.255 9.54 12.555C9.39 12.705 9.195 12.78 9.015 12.78L9 12.75Z"
                      fill="black"
                    />
                  </svg>
                </div>
                <div class="swiper-button-next tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3">
                  <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.25 7.5H18.75C19.17 7.5 19.5 7.17 19.5 6.75C19.5 6.33 19.17 6 18.75 6H5.25C4.83 6 4.5 6.33 4.5 6.75C4.5 7.17 4.83 7.5 5.25 7.5Z"
                      fill="black"
                    />
                    <path
                      d="M15 12.75C15.0984 12.7512 15.1959 12.7318 15.2863 12.6931C15.3767 12.6543 15.458 12.5971 15.525 12.525L20.775 7.275C21.075 6.975 21.075 6.51 20.775 6.21L15.525 0.975C15.225 0.675 14.76 0.675 14.46 0.975C14.16 1.275 14.16 1.74 14.46 2.04L19.185 6.765L14.46 11.49C14.16 11.79 14.16 12.255 14.46 12.555C14.61 12.705 14.805 12.78 14.985 12.78L15 12.75Z"
                      fill="black"
                    />
                  </svg>
                </div>
              </div>
            </Swiper>
          </div>
        </div>
        <div v-else class="tw-w-screen tw-px-4">
          <div class="tw-mt-16 tw-w-full" :class="!isMobileDevice ? 'tw-grid tw-grid-cols-12 tw-gap-x-3' : ''">
            <div
              v-if="!isMobileDevice"
              class="tw-col-span-12 tw-h-full tw-w-full lg:tw-col-span-6"
              @click="onClickedImage(null)"
            >
              <library-image
                :src="primarySevenPhotos[0].background"
                class="tw-h-full tw-cursor-pointer"
                alt="property first image"
                :title="property.advert_heading"
                @click="onClickedImage(primarySevenPhotos[0])"
                :design-width="948"
                :design-height="626"
                :style="`border-radius: var(--rounded)`"
                :blur="imageBlurFilter"
                :pre-market-blur-text="preMarketBlurText"
              />
            </div>

            <div
              v-if="!isMobileDevice"
              class="tw-col-span-12 tw-grid tw-h-full tw-grid-cols-12 tw-gap-x-3 md:tw-col-span-6"
            >
              <div class="tw-col-span-6 tw-flex tw-flex-row tw-gap-y-3 md:tw-flex-col">
                <library-image
                  :src="primarySevenPhotos[1].background"
                  alt="property first image"
                  class="tw-cursor-pointer"
                  @click="onClickedImage(primarySevenPhotos[1])"
                  :title="property.advert_heading"
                  :design-width="448"
                  :design-height="303"
                  :style="`border-radius: var(--rounded)`"
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                />

                <library-image
                  :src="primarySevenPhotos[2].background"
                  alt="property second image"
                  class="tw-cursor-pointer"
                  @click="onClickedImage(primarySevenPhotos[2])"
                  :title="property.advert_heading"
                  :design-width="448"
                  :design-height="303"
                  :style="`border-radius: var(--rounded)`"
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                />
              </div>
              <div class="tw-col-span-6 tw-flex tw-flex-row tw-gap-y-3 md:tw-flex-col">
                <library-image
                  :src="primarySevenPhotos[3].background"
                  class="tw-cursor-pointer"
                  @click="onClickedImage(primarySevenPhotos[4])"
                  :design-width="448"
                  :design-height="303"
                  :style="`border-radius: var(--rounded)`"
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                />

                <library-image
                  :src="primarySevenPhotos[4].background"
                  alt="property second image"
                  class="tw-cursor-pointer"
                  @click="onClickedImage(primarySevenPhotos[6])"
                  :title="property.advert_heading"
                  :design-width="448"
                  :design-height="303"
                  :style="`border-radius: var(--rounded)`"
                  :blur="imageBlurFilter"
                  :pre-market-blur-text="preMarketBlurText"
                >
                  <template #overlay-item>
                    <div
                      v-if="property && property.medias"
                      class="tw-absolute tw-inset-0 tw-z-10 tw-h-full tw-w-full tw-bg-black tw-opacity-30"
                      style="border-radius: var(--rounded-sm)"
                    ></div>
                    <button
                      class="tw-z-20 tw-px-4 tw-py-2"
                      style="background-color: var(--c-section_primary); color: var(--c-text-primary)"
                    >
                      SEE ALL PHOTOS
                    </button>
                  </template>
                </library-image>
              </div>
            </div>
            <div v-else-if="isMobileDevice" class="n-main-container tw-w-screen">
              <Swiper
                :modules="[SwiperNavigation]"
                :pagination="true"
                :loop="true"
                :slides-per-view="1"
                :space-between="1"
                effect="fade"
                :clickable="true"
                :navigation="{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }"
                class="tw-w-full"
                @swiper="onSwiper"
              >
                <SwiperSlide v-for="(item, index) in primaryFivePhotos" :key="index" class="tw-w-full">
                  <library-image :src="item.background" @click="onClickedImage(item)" :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText"/>
                </SwiperSlide>
                <div class="tw-flex tw-items-center tw-justify-between">
                  <div
                    class="swiper-button-prev tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3"
                  >
                    <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M18.75 7.5H5.25C4.83 7.5 4.5 7.17 4.5 6.75C4.5 6.33 4.83 6 5.25 6H18.75C19.17 6 19.5 6.33 19.5 6.75C19.5 7.17 19.17 7.5 18.75 7.5Z"
                        fill="black"
                      />
                      <path
                        d="M9 12.75C8.90164 12.7512 8.80411 12.7318 8.71369 12.6931C8.62328 12.6543 8.54197 12.5971 8.475 12.525L3.225 7.275C2.925 6.975 2.925 6.51 3.225 6.21L8.475 0.975C8.775 0.675 9.24 0.675 9.54 0.975C9.84 1.275 9.84 1.74 9.54 2.04L4.815 6.765L9.54 11.49C9.84 11.79 9.84 12.255 9.54 12.555C9.39 12.705 9.195 12.78 9.015 12.78L9 12.75Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                  <div
                    class="swiper-button-next tw-cursor-pointer tw-rounded-md tw-bg-white tw-px-2 tw-py-5 lg:tw-px-3"
                  >
                    <svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.25 7.5H18.75C19.17 7.5 19.5 7.17 19.5 6.75C19.5 6.33 19.17 6 18.75 6H5.25C4.83 6 4.5 6.33 4.5 6.75C4.5 7.17 4.83 7.5 5.25 7.5Z"
                        fill="black"
                      />
                      <path
                        d="M15 12.75C15.0984 12.7512 15.1959 12.7318 15.2863 12.6931C15.3767 12.6543 15.458 12.5971 15.525 12.525L20.775 7.275C21.075 6.975 21.075 6.51 20.775 6.21L15.525 0.975C15.225 0.675 14.76 0.675 14.46 0.975C14.16 1.275 14.16 1.74 14.46 2.04L19.185 6.765L14.46 11.49C14.16 11.79 14.16 12.255 14.46 12.555C14.61 12.705 14.805 12.78 14.985 12.78L15 12.75Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                </div>
              </Swiper>
            </div>
          </div>
        </div>
      </template>
      <!-- tabs -->
      <template v-if="!property.is_locked">
        <ul
          v-if="variant === variants.v1"
          class="nav nav-tabs pxp-nav-tabs tw-my-8 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-10 lg:tw-flex-row"
        >
          <li v-for="(tab, tabIndex) in tabs" :key="`tab-${tabIndex}`" class="nav-item">
            <div
              :class="activeTab.name === tab.name ? 'active' : ''"
              class="nav-link tw-cursor-pointer tw-font-bold"
              data-toggle="tab"
              @click="
                () => {
                  activeTab.name = tab.name;
                  activeTab.title = tab.title;
                }
              "
            >
              {{ tab.title }}
            </div>
          </li>
        </ul>
      </template>
      <template v-else-if="property.is_locked">
        <library-login />
      </template>
      <ul
        class="nav nav-tabs pxp-nav-tabs sticky-nav tw-my-8 tw-flex tw-min-h-16 tw-w-full tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-pb-2 tw-pt-4 tw-transition-all tw-duration-200 min-[480px]:tw-text-center md:tw-gap-4 lg:tw-flex-nowrap lg:tw-gap-5"
        :class="isScrolled ? 'tw-fixed tw-top-14 tw-z-30 tw-bg-white' : ''"
        v-if="variant === variants.v2 && !property.is_locked"
        ref="navTabs"
      >
        <li
          v-for="(tab, tabIndex) in tabsV2"
          :key="`tab2-${tabIndex}`"
          class="nav-item tw-flex-[40%] min-[480px]:tw-flex-[30%] sm:tw-flex-auto"
        >
          <div
            :class="activeTabV2.name === tab.name ? 'active' : ''"
            class="nav-link tw-cursor-pointer tw-font-bold"
            data-toggle="tab"
            @click="
              () => {
                activeTabV2.name = tab.name;
              }
            "
          >
            <nuxt-link :to="`#${tab.name}`" class="!tw-p-2">{{ tab.title }}</nuxt-link>
          </div>
        </li>
      </ul>
      <!-- tabs end -->
    </section>

    <!-- PROPERTY DETAIL -->
    <section
      v-if="variant === variants.v1 && !property.is_locked"
      class="n-section-primary !tw-pt-0"
      style="background-color: var(--c-section_primary)"
    >
      <div>
        <div class="tw-grid tw-grid-cols-12 md:tw-gap-x-10">
          <div class="tw-col-span-12 tw-flex tw-flex-col tw-gap-y-12 lg:tw-col-span-7 xl:tw-col-span-8">
            <div class="tab-content tw-mt-6">
              <div v-show="activeTab.name === 'features'">
                <h4 class="tw-pb-4">{{ activeTab.title }}</h4>
                <div v-if="property?.features" class="tw-mt-8 tw-grid tw-grid-cols-12 tw-gap-4">
                  <div
                    v-for="(feature, index) in property.features"
                    :key="`property-feature-${index}`"
                    class="tw-col-span-12 tw-flex tw-items-start tw-gap-x-3 md:tw-col-span-6 xl:tw-col-span-4"
                  >
                    <span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M9.5501 18L3.8501 12.3L5.2751 10.875L9.5501 15.15L18.7251 5.97498L20.1501 7.39998L9.5501 18Z"
                          fill="var(--c-tertiary)"
                        />
                      </svg>
                    </span>
                    <library-text class="tw-text-sm tw-font-semibold tw-uppercase" :text="feature"> </library-text>
                  </div>
                </div>
              </div>

              <div v-show="activeTab.name === 'property_information'" id="property_information" role="tabpanel">
                <h4 class="tw-pb-4">{{ activeTab.title }}</h4>

                <library-text class="tw-text-sm" :html="property.description"></library-text>

                <!-- amenties -->
                <library-properties-property-criteria-list
                  :property="property"
                  class="tw-mt-8"
                  main-list-title="Additional Information"
                  additional-list-title=""
                >
                  <template #list-item="{ criteria }">
                    <div class="tw-col-span-12 md:tw-col-span-6" style="">
                      <div class="tw-grid tw-grid-cols-2">
                        <p class="tw-col-span-1">{{ criteria.title }}:</p>
                        <p class="tw-col-span-1 tw-text-left !tw-font-bold tw-capitalize">&nbsp;{{ criteria.value }}</p>
                      </div>
                    </div>
                  </template>
                </library-properties-property-criteria-list>
              </div>

              <div v-show="activeTab.name === 'matterport_video'" id="matterport_video" role="tabpanel" class="tw-pb-8">
                <div v-if="property?.virtual_tour">
                  <h4 class="tw-pb-4">{{ activeTab.title }}</h4>
                  <library-iframe :src="matterPort" :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText" />
                </div>
              </div>

              <div v-show="activeTab.name === 'video_tour'" id="video_tour" role="tabpanel">
                <div v-if="featuredVideo">
                  <h4 class="tw-pb-4">{{ activeTab.title }}</h4>
                  <div class="tw-aspect-[9/16] tw-max-w-[420px]">
                    <library-iframe
                      class="tw-absolute tw-inset-0 tw-h-full tw-w-full"
                      :src="featuredVideo"
                      frameborder="0"
                      allowfullscreen
                      :blur="imageBlurFilter"
                      :pre-market-blur-text="preMarketBlurText"
                    ></library-iframe>
                  </div>
                </div>
              </div>

              <div v-show="activeTab.name === 'floor_plan'" id="floor_plan" role="tabpanel">
                <h4 class="tw-pb-4">{{ activeTab.title }}</h4>

                <floor-plan :data="property.floor_plans" :component="component" />
              </div>
              <div v-show="activeTab.name === 'map'" id="map" role="tabpanel">
                <h4 class="tw-pb-4">{{ activeTab.title }}</h4>

                <neuron-google-map
                  v-if="property.map_position && property.map_position.lat && property.map_position.lng"
                  v-model:map-center-position="property.map_position"
                  :component="component"
                  :marker-color="markerColor"
                />
              </div>
              <div v-show="activeTab.name === 'epc'" id="epc" role="tabpanel">
                <h4 class="tw-pb-4">{{ activeTab.title }}</h4>
                <epc :data="property.epcs" :component="component" />
              </div>
            </div>
          </div>
          <div class="tw-col-span-12 tw-pt-6 lg:tw-col-span-5 xl:tw-col-span-4">
            <div v-if="sidebarComponents && sidebarComponents.length" class="tw-flex tw-flex-col tw-gap-4">
              <div v-if="vars.viewingOptionsBool">
                <div
                  class="tw-relative tw-flex tw-w-full tw-flex-col tw-justify-center tw-gap-5 tw-px-4 tw-py-9 tw-text-start"
                  :style="`background-color: var(--c-sidebar-card); border-radius: var(--rounded);`"
                >
                  <h4>{{ vars.viewingOptionsTitleText }}</h4>
                  <p v-if="vars.viewingOptionsDescriptionText">
                    {{ vars.viewingOptionsDescriptionText }}
                  </p>
                  <div class="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
                    <button
                      v-if="featuredVideo"
                      :class="`n-${vars.viewingOptionsButton1TypeText} tw-w-fit`"
                      @click="onVidePopup()"
                    >
                      {{ vars.viewingOptionsButton1LabelText }}
                    </button>
                    <button :class="`n-${vars.viewingOptionsButton2TypeText} tw-w-fit`" @click="onSendLead()">
                      {{ vars.viewingOptionsButton2LabelText }}
                    </button>
                  </div>
                </div>
              </div>
              <theme6-available-components-for-sidebar
                key="property-detail-sidebar"
                key-prefix="property-detail-sidebar"
                :specific-components="sidebarComponents"
              />
            </div>
          </div>
        </div>
      </div>

      <full-screen-gallery
        v-if="fullScreenPopupConfig.is_active && activeMedia && property && property.medias"
        v-model:active-media-index="activeMediaIndex"
        :component="component"
        :active-media="activeMedia"
        :count-of-medias="property.medias.length"
        @close="closeLightbox()"
      />

      <popup :config="sendLeadPopupConfig" custom-width="800px">
        <template #content>
          <send-lead
            v-if="sendLeadPopupConfig.isActive"
            :property="property"
            @on-success="onSuccess"
            @close="sendLeadPopupConfig.isActive = false"
          />
        </template>
      </popup>

      <popup :config="videoPopupConfig" v-if="videoPopupConfig.videoPopupSrc" custom-width="500px" @close="stopVideo">
        <template #content>
          <div class="tw-relative tw-pt-6">
            <div class="tw-aspect-[9/16] tw-h-full tw-w-full">
              <library-iframe
                class="tw-absolute tw-inset-0 tw-h-full tw-w-full"
                :src="videoPopupConfig.videoPopupSrc"
                frameborder="0"
                allowfullscreen
                :blur="imageBlurFilter"
                :pre-market-blur-text="preMarketBlurText"
              ></library-iframe>
            </div>
          </div>
        </template>
      </popup>

      <popup :config="successModalConfig" @close="onSuccessModalClosed()">
        <template #content>
          <success :text="successPopupText" />
        </template>
      </popup>
    </section>

    <section
      v-if="variant === variants.v2 && !property.is_locked"
      class="n-section-primary !tw-pt-0"
      style="background-color: var(--c-section_primary)"
    >
      <div>
        <div class="tw-grid tw-grid-cols-12 md:tw-gap-x-10">
          <div class="tw-col-span-12 tw-flex tw-flex-col tw-gap-y-12 lg:tw-col-span-7 xl:tw-col-span-8">
            <div class="tab-content tw-mt-6">
              <div id="cinematic_videography" role="tabpanel" class="tw-pb-8">
                <div v-if="featuredVideo">
                  <h4 class="tw-pb-4">CINEMATIC VIDEOGRAPHY</h4>

                  <library-iframe :src="featuredVideo" :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText" />
                </div>
              </div>

              <div id="features" role="tabpanel" class="tw-pb-8">
                <h4 class="tw-pb-4">FEATURES</h4>

                <div v-if="property?.features" class="tw-mt-8 tw-grid tw-grid-cols-12 tw-gap-4">
                  <div
                    v-for="(feature, index) in property.features"
                    :key="`property-feature-${index}`"
                    class="tw-col-span-12 tw-flex tw-items-start tw-gap-x-3 md:tw-col-span-6 xl:tw-col-span-4"
                  >
                    <span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M9.5501 18L3.8501 12.3L5.2751 10.875L9.5501 15.15L18.7251 5.97498L20.1501 7.39998L9.5501 18Z"
                          fill="var(--c-primary)"
                        />
                      </svg>
                    </span>
                    <library-text class="tw-text-sm tw-font-semibold tw-uppercase" :text="feature"> </library-text>
                  </div>
                </div>
              </div>
              <div id="property_information" role="tabpanel" class="tw-pb-8">
                <h4 class="tw-pb-4">PROPERTY INFO</h4>

                <library-text class="tw-text-sm" :html="property.description"></library-text>

                <!-- amenties -->
                <div v-if="property?.criteria_list" class="tw-grid tw-grid-cols-12 tw-gap-4 tw-pt-6">
                  <div
                    v-for="(criteria, index) in property?.criteria_list"
                    :key="`criteria-${index}`"
                    class="tw-col-span-12 md:tw-col-span-6"
                  >
                    <div class="tw-grid tw-grid-cols-2">
                      <p class="tw-col-span-1">{{ criteria.title }}:</p>
                      <p class="tw-col-span-1 tw-text-left !tw-font-bold tw-capitalize">&nbsp;{{ criteria.value }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!--               <div id="matterport_video" role="tabpanel" class="tw-pb-8">
                <div v-if="property?.virtual_tour">
                  <h4 class="tw-pb-4">MATTERPORT VIDEO</h4>
                  <library-iframe :src="matterPort" />
                </div>
              </div> -->

              <div id="floor_plan" role="tabpanel" class="tw-pb-8">
                <h4 class="tw-pb-4">FLOOR PLANS</h4>

                <floor-plan :data="property.floor_plans" :component="component" />
              </div>
              <div id="map" role="tabpanel" class="tw-pb-8">
                <h4 class="tw-pb-4">MAP</h4>

                <neuron-google-map
                  v-if="property.map_position && property.map_position.lat && property.map_position.lng"
                  v-model:map-center-position="property.map_position"
                  :component="component"
                  :marker-color="markerColor"
                />
              </div>
              <div v-if="property?.epcs?.length" id="epc" role="tabpanel" class="tw-pb-8">
                <h4 class="tw-pb-4">EPC</h4>
                <epc :data="property.epcs" :component="component" />
              </div>
            </div>
          </div>
          <!-- Property Video, Send Lead: START -->
          <div class="tw-col-span-12 tw-pt-6 lg:tw-col-span-5 xl:tw-col-span-4">
            <div v-if="sidebarComponents && sidebarComponents.length" class="tw-flex tw-flex-col tw-gap-4">
              <div v-if="vars.viewingOptionsBool">
                <div
                  class="tw-relative tw-flex tw-w-full tw-flex-col tw-justify-center tw-gap-5 tw-px-4 tw-py-9 tw-text-start"
                  :style="`background-color: var(--c-sidebar-card); border-radius: var(--rounded);`"
                >
                  <h4>{{ vars.viewingOptionsTitleText }}</h4>
                  <p v-if="vars.viewingOptionsDescriptionText">
                    {{ vars.viewingOptionsDescriptionText }}
                  </p>
                  <div class="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
                    <button :class="`n-${vars.viewingOptionsButton1TypeText} tw-w-fit`" @click="onVidePopup()">
                      {{ vars.viewingOptionsButton1LabelText }}
                    </button>
                    <button :class="`n-${vars.viewingOptionsButton2TypeText} tw-w-fit`" @click="onSendLead()">
                      {{ vars.viewingOptionsButton2LabelText }}
                    </button>
                  </div>
                </div>
              </div>
              <theme6-available-components-for-sidebar
                key="property-detail-sidebar"
                key-prefix="property-detail-sidebar"
                :specific-components="sidebarComponents"
              />
            </div>
          </div>
        </div>
      </div>

      <full-screen-gallery
        v-if="fullScreenPopupConfig.is_active && activeMedia && property && property.medias"
        v-model:active-media-index="activeMediaIndex"
        :component="component"
        :active-media="activeMedia"
        :count-of-medias="property.medias.length"
        @close="closeLightbox()"
      />

      <popup :config="videoPopupConfig" custom-width="800px" @close="stopVideo">
        <template #content>
          <div class="tw-relative tw-pt-6">
            <library-iframe :src="videoPopupConfig.videoPopupSrc" frameborder="0" allowfullscreen :blur="imageBlurFilter" :pre-market-blur-text="preMarketBlurText"></library-iframe>
          </div>
        </template>
      </popup>

      <popup :config="sendLeadPopupConfig" custom-width="800px">
        <template #content>
          <send-lead
            v-if="sendLeadPopupConfig.isActive"
            :property="property"
            @on-success="onSuccess"
            @close="sendLeadPopupConfig.isActive = false"
          />
        </template>
      </popup>
      <popup :config="successModalConfig" @close="onSuccessModalClosed()">
        <template #content>
          <success :text="successPopupText" />
        </template>
      </popup>
    </section>
  </div>
</template>

<script lang="ts">
import { mapActions } from 'pinia';
import { ROUTES } from '~/constants/ui.constants';
import { usePropertiesStore } from '~/units/properties/stores';
import type { Component } from '~/types';
import { ComponentMixin } from '~/mixins/component.mixin';
import { PropertyMixin } from '~/units/properties/mixins/property.mixin';
import FloorPlan from '~/components/common/FloorPlan.vue';
import NeuronGoogleMap from '~/components/common/NeuronGoogleMap.vue';
import Epc from '~/components/common/Epc.vue';
import NcSelect from '~/components/common/inputs/NcSelect.vue';
import NcInput from '~/components/common/inputs/NcInput.vue';
import NcButton from '~/components/common/inputs/NcButton.vue';
import FullScreenGallery from '~/components/common/FullScreenGallery.vue';
import { useCoreStore } from '~/units/core/store';
import SendLead from '~/components/common/leads/SendLead.vue';
import Success from '~/components/common/popup/Success.vue';
import Popup from '~/components/common/popup/Popup.vue';
import PropertyDetailActions from '~/components/theme4/properties/PropertyDetailActions.vue';
import AvailableComponentsForSidebar from '~/components/theme4/AvailableComponentsForSidebar.vue';
import { FullScreenPopupMixin } from '~/units/core/mixins/fullScreenPopupMixin';
import { getImagesByCount } from '~/units/properties/store';

export default defineNuxtComponent({
  name: 'Theme6PropertyDetail',
  components: {
    AvailableComponentsForSidebar,
    PropertyDetailActions,
    Popup,
    Success,
    SendLead,
    FullScreenGallery,
    NcButton,
    NcInput,
    NcSelect,
    Epc,
    NeuronGoogleMap,
    FloorPlan,
  },

  mixins: [ComponentMixin, PropertyMixin, FullScreenPopupMixin],

  data() {
    return {
      activeTab: { name: 'features', title: 'FEATURES', is_visible: !!this.featuredVideo },
      activeTabV2: { name: 'features', title: 'FEATURES' },
      swiper: {},
      isScrolled: false,
      scrollThreshold: 0,
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
      videoPopupConfig: {
        isActive: false,
        videoPopupSrc: this.featuredVideo,
      },
    };
  },

  mounted() {
    this.setScrollThreshold();
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  },

  computed: {
    tabs() {
      const tabs = [
        {
          name: 'features',
          title: 'FEATURES',
          is_visible: !!this.property?.features,
        },
        {
          name: 'property_information',
          title: 'PROPERTY INFO',
          is_visible: true,
        },
        {
          name: 'floor_plan',
          title: 'FLOOR PLANS',
          is_visible: !!this.property?.floor_plans?.length,
        },
        {
          name: 'map',
          title: 'MAP',
          is_visible: !!this.property.map_position,
        },
        {
          name: 'epc',
          title: 'EPC',
          is_visible: !!this.property?.epcs?.length,
        },
        {
          name: 'matterport_video',
          title: 'VIRTUAL TOUR',
          is_visible: !!this.matterPort,
        },
        {
          name: 'video_tour',
          title: 'VIDEO TOUR',
          is_visible: !!this.featuredVideo,
        },
      ];

      return tabs.filter((i) => i.is_visible);
    },

    tabsV2() {
      const tabs = [
        {
          name: 'cinematic_videography',
          title: 'CINEMATIC VIDEO',
          is_visible: !!this.featuredVideo,
        },
        {
          name: 'features',
          title: 'FEATURES',
          is_visible: !!this.property?.features,
        },
        {
          name: 'property_information',
          title: 'PROPERTY INFO',
          is_visible: true,
        },
        {
          name: 'floor_plan',
          title: 'FLOOR PLANS',
          is_visible: !!this.property?.floor_plans?.length,
        },
        {
          name: 'map',
          title: 'MAP',
          is_visible: !!this.property.map_position,
        },
        {
          name: 'epc',
          title: 'EPC',
          is_visible: !!this.property?.epcs?.length,
        },
        /*         {
          name: 'matterport_video',
          title: 'MATTERPORT VIDEO',
          is_visible: !!this.matterPort,
        }, */
      ];

      return tabs.filter((i) => i.is_visible);
    },

    markerColor() {
      return this.colors.primary;
    },

    ROUTES() {
      return ROUTES;
    },

    primaryFivePhotos() {
      if (this.property) {
        return getImagesByCount(this.property, 5);
      }
      return [];
    },

    primarySevenPhotos() {
      if (this.property) {
        return getImagesByCount(this.property, 7);
      }
      return [];
    },

    sidebarComponents(): Component[] {
      if (this.currentPage && this.currentPage.sidebar) {
        return this.currentPage.sidebar;
      }
      return [];
    },

    isMortgageCalculatorVisible() {
      return !this.vars.isMortgageCalculatorHiddenNumber;
    },

    isStampDutyCalculatorVisible() {
      return !this.vars.isStampDutyCalculatorHiddenNumber;
    },

    isLetterPropertyCriteria() {
      return this.vars.isLetterPropertyCriteriaNumber;
    },

    featuredVideo() {
      const mediaLink = this.property.featured_video?.mediaLink;
      if (mediaLink && mediaLink.includes('matterport')) {
        return null;
      }
      return mediaLink;
    },

    matterPort() {
      let mediaLink = this.property?.virtual_tour?.mediaLink;
      if (mediaLink && mediaLink.includes('youtu')) {
        return null;
      }
      if (mediaLink && mediaLink.startsWith('http://')) {
        mediaLink = mediaLink.replace('http://', 'https://');
      }
      return mediaLink;
    },
  },

  methods: {
    ...mapActions(useCoreStore, ['shareApi', 'openTab']),
    async onShared() {
      await this.shareApi({
        title: this.property?.advert_heading,
        text: this.property?.advert_heading,
        url: window.location.href,
      });
    },

    onSwiper(swiper: object) {
      this.swiper = swiper;
    },

    href(location) {
      this.activeTab.name = location;
    },

    handleScroll() {
      this.isScrolled = window.scrollY > this.scrollThreshold;
    },

    setScrollThreshold() {
      nextTick(() => {
        const ulElement = this.$refs.navTabs;
        if (ulElement) {
          this.scrollThreshold = ulElement.offsetTop - 90;
        }
      });
    },

    onVidePopup() {
      this.videoPopupConfig.isActive = !this.videoPopupConfig.isActive;
      this.videoPopupConfig.videoPopupSrc = this.featuredVideo;
    },

    stopVideo() {
      this.videoPopupConfig.videoPopupSrc = '';
    },
  },

  async setup(props: { component: Component }) {
    const store = usePropertiesStore();
    await store.fetchSingleProperty(props.component);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
});
</script>

<style scoped>
.share-button {
  border: 2px solid var(--camel);
}

.nav-link.active {
  color: var(--purple);
  border-bottom-color: var(--purple);
}

.nav-link {
  color: var(--black);
  font-size: 16px;
}

.nc-first-image-overlay-item {
  position: absolute;
  bottom: 0.1rem;
  right: 0.1rem;
  transform: translate(-50%, -50%);
  text-align: right;
  background-color: var(--white);
  color: var(--nc-classic-black);
  border-radius: 4px;
}

.nc-all-photos-button {
  position: absolute;
  bottom: 20px;
  left: 70px;
  padding: 10px;
  border-radius: 4px;
  background-color: var(--white);
  color: var(--nc-classic-black);
}

.photo-next,
.photo-prev {
  position: absolute;
  top: 50%;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  width: 44px;
  height: 64px;
  background-color: #fff;
  border-radius: var(--rounded);
  -webkit-transform: translate(0, -50%);
  transform: translate(0, -50%);
  cursor: pointer;
  font-size: 26px;
}

.photo-next {
  right: 5%;
}

.photo-prev {
  left: 5%;
}

::v-deep(.floor-tab .floor-plan-items .floor-plan-card) {
  width: 100% !important;
}

::v-deep(.floor-tab .floor-plan-items .floor-plan-card figure) {
  border: none !important;
}

::v-deep(.floor-tab) {
  padding: unset !important;
}

::v-deep(.inline-iframe-container iframe) {
  height: 590px;
}

.theme4-floor-plan ::v-deep(figure) {
  height: unset !important;
}

.swiper-button-next::after,
.swiper-button-prev::after {
  content: unset;
}
.swiper-button-next,
.swiper-button-prev {
  width: unset;
  height: unset;
}

::v-deep(img) {
  border-radius: 4px;
}

.property-detail-card {
  padding: 24px;
  border-radius: var(--rounded);
  background-color: var(--c-section_primary);
}

.property-detail-card.accordion {
  padding-bottom: 0;
  padding-top: 0;
}

@media screen and (min-width: 1024px) {
  .property-detail-card {
    padding: 32px;
  }

  .property-detail-card.accordion {
    padding-bottom: 0;
    padding-top: 0;
  }
}

.accordion-stamp-duty-calculator ::v-deep(h3),
.accordion-mortgage-calculator ::v-deep(h3) {
  display: none;
}

.accordion-stamp-duty-calculator ::v-deep(.stamp-duty-result),
.accordion-mortgage-calculator ::v-deep(.mortgage-result) {
  background: var(--c-primary);
  border-radius: var(--rounded);
}
.accordion-stamp-duty-calculator ::v-deep(select),
.accordion-stamp-duty-calculator ::v-deep(.n-input),
.accordion-stamp-duty-calculator ::v-deep(input),
.accordion-mortgage-calculator ::v-deep(select),
.accordion-mortgage-calculator ::v-deep(.n-input),
.accordion-mortgage-calculator ::v-deep(input) {
  background: var(--c-input_secondary) !important;
}

.nav-link:hover {
  color: var(--c-primary);
}

.active {
  color: var(--c-primary) !important;
}
</style>
